import React, { useEffect } from 'react';

/* 
Props:
- keyMap: object. Example: { Delete: handleKeyDown, 'Meta+Backspace': handleKeyDown, Enter: handleKeyDown },
- eventType: string. Default: 'keydown'
- global: boolean
- preventDefault: boolean or function. Default: false
- stopPropagation: boolean or function. Default: false
- target: string (selector) or object (ref). Default: 'document'
- children: ReactNode
*/

const KeyHandler = ({ keyMap, eventType = 'keydown', active = true, global = false, preventDefault = false, stopPropagation = false, target = 'document', capture = false, children }) => {
  const handleEvent = (event) => {
    const shiftKey = event.shiftKey ? 'Shift+' : '';
    const metaKey = event.metaKey ? 'Meta+' : '';
    const controlKey = event.ctrlKey ? 'Control+' : '';
    const altKey = event.altKey ? 'Alt+' : '';
    const eventKey = event.key !== 'Shift' && event.key !== 'Meta' && event.key !== 'Control' && event.key !== 'Alt' ? event.key : '';
    const keyAction = `${shiftKey}${controlKey}${metaKey}${altKey}${eventKey}`;
    // eslint-disable-next-line no-prototype-builtins
    if (keyMap.hasOwnProperty(keyAction)) {
      if (preventDefault === true || (typeof preventDefault === 'function' && preventDefault(keyAction))) {
        event.preventDefault();
      }
      if (stopPropagation === true || (typeof stopPropagation === 'function' && stopPropagation(keyAction))) {
        event.stopPropagation();
      }
      keyMap[keyAction](event, keyAction);
      return;
    }
    if (keyMap.Default) {
      keyMap.Default(event, keyAction);
    }
  };

  useEffect(() => {
    // Determine the target for event listeners: document, ref's current, or a query-selected element
    let eventTarget;
    if (global) {
      eventTarget = window;
    } else if (target && typeof target === 'object' && target.current) {
      // Handling ref.current
      eventTarget = target.current;
    } else if (typeof target === 'string') {
      // Handling selector string
      eventTarget = document.querySelector(target);
    } else {
      console.warn('KeyHandler: Invalid target. Falling back to global (window).');
      eventTarget = window;
    }

    if (eventTarget && active) {
      eventTarget?.addEventListener(eventType, handleEvent, { capture });
    }

    return () => {
      if (eventTarget) {
        eventTarget?.removeEventListener(eventType, handleEvent, { capture });
      }
    };
  }, [keyMap, eventType, global, preventDefault, stopPropagation, target, active, capture]);

  // For global key handlers that do not wrap any children, this component renders nothing.
  // For scoped key handlers, render the children as-is.
  return !global && children ? <>{children}</> : null;
};

export default KeyHandler;
